<template>
  <div class="language-switcher">
    <!-- Version compacte pour sidebar -->
    <div v-if="compact" class="relative group">
      <button
        @click="toggleDropdown"
        class="w-10 h-10 rounded-lg flex items-center justify-center text-white hover:bg-blue-800 transition-colors"
        :title="currentLanguage.name"
      >
        <span class="text-lg">{{ currentLanguage.flag }}</span>
      </button>
      
      <!-- Dropdown -->
      <div 
        v-if="showDropdown"
        class="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[120px] z-50"
      >
        <button
          v-for="lang in languages"
          :key="lang.code"
          @click="changeLanguage(lang.code)"
          class="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
          :class="{ 'bg-blue-50 text-blue-600': locale === lang.code }"
        >
          <span>{{ lang.flag }}</span>
          <span>{{ lang.name }}</span>
        </button>
      </div>
    </div>

    <!-- Version normale -->
    <select 
      v-else
      :value="locale" 
      @change="handleSelectChange"
      class="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    >
      <option v-for="lang in languages" :key="lang.code" :value="lang.code">
        {{ lang.flag }} {{ lang.name }}
      </option>
    </select>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

interface Props {
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  compact: false
});

const { locale } = useI18n();
const switchLocalePath = useSwitchLocalePath();
const showDropdown = ref(false);

const languages = [
  { code: 'fr-FR', name: 'Français', flag: '🇫🇷' },
  { code: 'en-GB', name: 'English', flag: '🇬🇧' }
];

const currentLanguage = computed(() => {
  return languages.find(lang => lang.code === locale.value) || languages[0];
});

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const handleSelectChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  if (target?.value) {
    changeLanguage(target.value);
  }
};

const changeLanguage = async (newLocale: string) => {
  // Sauvegarder la préférence dans localStorage
  if (process.client) {
    localStorage.setItem('preferred-language', newLocale);
  }
  
  // Fermer le dropdown AVANT la navigation pour éviter les conflits
  showDropdown.value = false;
  
  // Obtenir le nouveau chemin
  const newPath = switchLocalePath(newLocale as 'fr-FR' | 'en-GB');
  console.log('🔄 Language switch:', { from: locale.value, to: newLocale, newPath });
  
  if (newPath && newPath !== window.location.pathname) {
    // Utiliser window.location pour une navigation complète (rechargement i18n)
    window.location.href = newPath;
  } else {
    // Fallback : forcer le changement de locale manuellement
    locale.value = newLocale;
  }
};

// Fermer le dropdown si on clique ailleurs
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  if (!target.closest('.language-switcher')) {
    showDropdown.value = false;
  }
};

// Charger la préférence au montage
onMounted(() => {
  if (props.compact) {
    document.addEventListener('click', handleClickOutside);
  }
});

onUnmounted(() => {
  if (props.compact) {
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<style scoped>
.language-switcher {
  position: relative;
}

/* Animation pour le dropdown */
.language-switcher div {
  transition: all 0.2s ease;
}
</style>
